import { AfterViewInit, Component, ViewEncapsulation } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  Validators,
  ValidatorFn,
} from '@angular/forms';

import { PaymentService } from '../../../services/payments.service';
import { CreditCard } from '../../../models/credit-card.model';
import { CreditCardFormValue } from '../../../interfaces/credit-card-form-value';

import { Country } from '../../../models/country.model';
import { ConfigService } from '../../../services/config.service';
import {
  GAPaymentType,
  GoogleAnalytics,
} from '../../../services/google-analytics.service';
import { Router } from '@angular/router';
import { OrderService } from '../../../services/order.service';

// pattern="^\d{5}(?:[\-]\d{4})?$" required
const ZIP_PATTERN = /^\d{5}(?:[\-]\d{4})?$/;
const CVV_PATTERN = /^[0-9]{3,4}$/;

function validatorFn(pattern: RegExp): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null =>
    validatorRule(control, pattern);
}

function validatorRule(control: AbstractControl, pattern: RegExp) {
  if (!control.value) {
    return { required: 'required' };
  } else if (!pattern.test(control.value)) {
    return { pattern: 'pattern' };
  }

  return null;
}

/**
 * zip validator only for US
 */
function zipCodeValidatorFn(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null => {
    const countryCode = control.root.value['country'];
    if (countryCode === 'US') {
      return validatorRule(control, ZIP_PATTERN);
    }

    return null;
  };
}

function expireDateValidatorFn(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null => {
    if (!control.value) {
      return { required: 'required' };
    }

    const current = new Date();
    const [month, year] = control.value.split('/');

    if (
      control.value.replace(/\//, '').length < 4 ||
      Number(current.getFullYear().toString().substring(2)) > Number(year)
    ) {
      return { pattern: 'pattern' };
    }

    if (
      Number(current.getFullYear().toString().substring(2)) >= Number(year) &&
      Number(current.getMonth() + 1) > Number(month?.replace(/^0+/, ''))
    ) {
      return { pattern: 'pattern' };
    }

    return null;
  };
}

@UntilDestroy()
@Component({
  selector: 'app-credit-card-modal',
  templateUrl: './credit-card-modal.component.html',
  styleUrls: ['./credit-card-modal.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class CreditCardModalComponent implements AfterViewInit {
  selectedCountry: string;
  card: CreditCard;
  orderId: number;
  saveToPaymentMethods = false;
  cardForm = new FormGroup({
    cardholderName: new FormControl('', [Validators.required]),
    label: new FormControl('', [Validators.required]),
    ccNumber: new FormControl('', [Validators.required]),
    cvv: new FormControl('', [validatorFn(CVV_PATTERN)]),
    expiresAt: new FormControl('', [expireDateValidatorFn()]),
    street: new FormControl('', [Validators.required]),
    city: new FormControl('', [Validators.required]),
    state: new FormControl(''),
    postalCode: new FormControl('', [zipCodeValidatorFn()]),
    country: new FormControl('', [Validators.required]),
  });

  countries: Country[] = [];
  fetchingCountries = false;

  constructor(
    private router: Router,
    private activeModal: NgbActiveModal,
    private configService: ConfigService,
    private paymentService: PaymentService,
    private orderSerivce: OrderService,
    private googleAnalytics: GoogleAnalytics
  ) {}

  ngAfterViewInit() {
    this.getCountries();
  }

  onSelectCountry(event: any) {
    if (event instanceof Country) {
      this.selectedCountry = event.isoCode;
    }
  }

  onHideDialog() {
    this.hideDialog();
  }

  onInputCCNumber(event: MouseEvent | any): void {
    const regex = /^(\d{0,4})(\d{0,4})(\d{0,4})(\d{0,4})(\d{0,3})$/g;
    const onlyNumbers = event.target.value.replace(/[^\d]/g, '');
    event.target.value = onlyNumbers.replace(regex, (_, $1, $2, $3, $4, $5) =>
      [$1, $2, $3, $4, $5].filter((group) => !!group).join(' ')
    );
  }

  onInputExpireDate(event: MouseEvent | any): void {
    event.target.value = event.target.value
      .replace(
        /[^0-9]/g,
        '' // To allow only numbers
      )
      .replace(
        /^([2-9])$/g,
        '0$1' // To handle 3 > 03
      )
      .replace(
        /^(1{1})([3-9]{1})$/g,
        '0$1/$2' // 13 > 01/3
      )
      .replace(
        /^0{1,}/g,
        '0' // To handle 00 > 0
      )
      .replace(
        /^([0-1]{1}[0-9]{1})([0-9]{1,2}).*/g,
        '$1/$2' // To handle 113 > 11/3
      );
  }

  onInputCVV(event: MouseEvent | any): void {
    event.target.value = event.target.value.replace(/[^\d]/g, '');
  }

  onSaveCreditCard(event: MouseEvent | any): void {
    event.preventDefault();

    if (!this.selectedCountry) {
      this.selectedCountry = '';
    }

    this.cardForm.markAllAsTouched();

    if (this.cardForm.valid) {
      this.paymentService
        .saveCreditCard(
          this.card?.id || 0,
          {
            ...this.formatFormValue(this.cardForm.value),
            ...(this.orderId && { order_id: this.orderId }),
          } as CreditCardFormValue,
          this.saveToPaymentMethods
        )
        .pipe(untilDestroyed(this))
        .subscribe((result: CreditCard) => {
          if (result) {
            this.hideDialog(result);

            this.googleAnalytics.addPaymentInfo(
              this.orderSerivce.activeOrder,
              GAPaymentType.CreditCard
            );
          }
        });
    }
  }

  validationClassName(control: AbstractControl): string {
    if (control.touched && control.invalid) {
      return control.invalid ? 'input-danger' : '';
    }
    return '';
  }

  get isSavingCreditCard(): boolean {
    return this.paymentService.savingNewCreditCard;
  }

  get cardholderName(): FormControl {
    return this.cardForm.get('cardholderName') as FormControl;
  }

  get label(): FormControl {
    return this.cardForm.get('label') as FormControl;
  }

  get ccNumber(): FormControl {
    return this.cardForm.get('ccNumber') as FormControl;
  }

  get expiresAt(): FormControl {
    return this.cardForm.get('expiresAt') as FormControl;
  }

  get cvv(): FormControl {
    return this.cardForm.get('cvv') as FormControl;
  }

  get street(): FormControl {
    return this.cardForm.get('street') as FormControl;
  }

  get city(): FormControl {
    return this.cardForm.get('city') as FormControl;
  }

  get postalCode(): FormControl {
    return this.cardForm.get('postalCode') as FormControl;
  }

  get state(): FormControl {
    return this.cardForm.get('state') as FormControl;
  }

  get country(): FormControl {
    return this.cardForm.get('country') as FormControl;
  }

  private hideDialog(result: CreditCard = null): void {
    this.activeModal.close(result);
  }

  private formatFormValue(value: { [key: string]: string }) {
    return {
      label: value.label,
      name: value.cardholderName,
      cc_number: value.ccNumber.replace(/ /g, ''),
      expires_at: value.expiresAt.replace(/\//g, ''),
      cvv: value.cvv,
      address1: value.street,
      country: this.selectedCountry,
      region: value.state,
      postal_code: value.postalCode,
      city: value.city,
    };
  }

  private getCountries() {
    this.fetchingCountries = true;
    this.configService
      .getBillingCountries()
      .pipe(untilDestroyed(this))
      .subscribe((result) => {
        this.fetchingCountries = false;
        this.countries = result;
      });
  }
}
